/**
 * useScreenWakeLock — Phase 3.8 mobile UX polish.
 *
 * Holds a "screen" wake lock while `active` is true so the phone does
 * not auto-lock during an active match (e.g. while a player is reading
 * the board, waiting for the bot, or watching opponent reveals).
 *
 * Feature-detected. Silently no-ops where unsupported (iOS Safari < 16.4,
 * older Firefox). Never blocks gameplay.
 *
 * Re-acquires on document `visibilitychange` (browsers release the lock
 * when the tab is hidden; we reacquire when it comes back AND we're
 * still in an active match).
 */
import { useEffect, useRef } from 'react';

export function isWakeLockSupported() {
  return typeof navigator !== 'undefined' && 'wakeLock' in navigator;
}

export function useScreenWakeLock(active) {
  const sentinelRef = useRef(null);
  const activeRef = useRef(active);

  useEffect(() => { activeRef.current = active; }, [active]);

  useEffect(() => {
    if (!isWakeLockSupported()) return undefined;

    let cancelled = false;

    const acquire = async () => {
      if (!activeRef.current) return;
      if (sentinelRef.current) return; // already held
      try {
        // navigator.wakeLock.request returns a WakeLockSentinel.
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled) {
          try { await sentinel.release(); } catch (_) { /* ignore */ }
          return;
        }
        sentinelRef.current = sentinel;
        sentinel.addEventListener?.('release', () => {
          // Browser released the lock (tab hidden, OS pressure). Clear
          // our ref; visibilitychange handler will reacquire when safe.
          if (sentinelRef.current === sentinel) sentinelRef.current = null;
        });
      } catch (_err) {
        // Permission denied / page not visible / unsupported — ignore.
      }
    };

    const release = async () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (!s) return;
      try { await s.release(); } catch (_) { /* ignore */ }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible' && activeRef.current) {
        // Re-acquire if we lost it while hidden.
        acquire();
      }
    };

    if (active) {
      acquire();
      document.addEventListener('visibilitychange', onVisibility);
    }

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      release();
    };
  }, [active]);
}
